<script setup lang="ts">
import { Bell, LogOut, Menu, Moon, PanelLeft, Sun, User } from 'lucide-vue-next'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { useAuthStore } from '@/stores/auth.store'
import { useUiStore } from '@/stores/ui.store'
import SidebarNav from './SidebarNav.vue'

const ui = useUiStore()
const auth = useAuthStore()
const router = useRouter()
const mobileNavOpen = ref(false)

const initials = computed(() => {
  const name = auth.user?.name ?? auth.user?.username ?? '?'
  return name
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
})

async function onLogout() {
  await auth.logout()
  router.replace({ name: 'login' })
}
</script>

<template>
  <header
    class="bg-background sticky top-0 z-30 flex h-16 items-center gap-3 border-b px-4 lg:px-6"
  >
    <!-- Mobile menu -->
    <Sheet v-model:open="mobileNavOpen">
      <SheetTrigger as-child>
        <Button variant="ghost" size="icon" class="lg:hidden">
          <Menu class="size-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="left" class="bg-sidebar w-72 p-0">
        <SheetTitle class="sr-only">Navigasi Utama</SheetTitle>
        <SheetDescription class="sr-only">Menu navigasi samping aplikasi POS</SheetDescription>
        <SidebarNav :on-navigate="() => (mobileNavOpen = false)" />
      </SheetContent>
    </Sheet>

    <!-- Perkecil/perlebar sidebar. Desktop saja: di mobile sidebar-nya berupa
         Sheet yang memang sudah tertutup secara default. -->
    <Button variant="ghost" size="icon" class="hidden lg:inline-flex" @click="ui.toggleSidebar()">
      <PanelLeft class="size-5" />
      <span class="sr-only">
        {{ ui.sidebarCollapsed ? 'Lebarkan menu samping' : 'Perkecil menu samping' }}
      </span>
    </Button>

    <div class="flex-1" />

    <Button variant="ghost" size="icon" @click="ui.toggleTheme()">
      <Sun v-if="ui.isDark" class="size-5" />
      <Moon v-else class="size-5" />
      <span class="sr-only">Ganti tema</span>
    </Button>

    <Button variant="ghost" size="icon">
      <Bell class="size-5" />
      <span class="sr-only">Notifikasi</span>
    </Button>

    <DropdownMenu>
      <DropdownMenuTrigger as-child>
        <button class="focus-visible:ring-ring rounded-full outline-none focus-visible:ring-2">
          <Avatar class="size-9">
            <AvatarFallback class="bg-primary/15 text-primary font-medium">
              {{ initials }}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" class="w-56">
        <DropdownMenuLabel>
          <div class="flex flex-col">
            <span class="font-medium">{{ auth.user?.name ?? auth.user?.username }}</span>
            <span class="text-muted-foreground text-xs">
              {{ auth.user?.role?.name ?? (auth.user?.isSuperadmin ? 'Superadmin' : '') }}
              <template v-if="auth.user?.tenant"> · {{ auth.user.tenant.name }}</template>
            </span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem> <User class="mr-2 size-4" /> Profil </DropdownMenuItem>
        <DropdownMenuItem @click="onLogout">
          <LogOut class="mr-2 size-4" /> Keluar
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </header>
</template>
